import { useId, useRef, useState } from 'react';
import { KeyboardIcon } from 'lucide-react';
import type { AskUserToolInputDTO, AskUserToolOutputDTO } from '@shared/schemas/chat-ask-user';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  buildFreeTextWidgetOutput,
  buildWidgetAskUserOutput,
  type AskUserProgress,
} from '../chat-ask-user-flow';
import { useAskUserShortcuts } from '../hooks/useAskUserShortcuts';
import {
  ASK_USER_ROW_ATTRIBUTE,
  AskUserSkeleton,
  FreeTextRow,
  OptionRow,
} from './AskUserOptionRows';
import './ask-user.css';

type AskUserWidgetProps = {
  allowFreeText?: boolean;
  allowMultiple?: boolean;
  hasContinuationError?: boolean;
  isSubmitting?: boolean;
  onRetry?: () => void;
  onSubmit: (output: AskUserToolOutputDTO) => void;
  options?: AskUserToolInputDTO['options'];
  /** Progression « n sur N » quand plusieurs questions attendent dans le même tour. */
  progress?: AskUserProgress | null;
  question?: string;
  state?: 'input-available' | 'input-streaming' | 'output-error';
};

/**
 * Carte d'une question en attente. Une fois répondue, la carte disparaît : la
 * réponse est rendue comme une bulle utilisateur (`AskUserAnswerBubble`).
 */
export function AskUserWidget({
  allowFreeText = false,
  allowMultiple = false,
  hasContinuationError = false,
  isSubmitting = false,
  onRetry,
  onSubmit,
  options = [],
  progress = null,
  question = '',
  state = 'input-available',
}: AskUserWidgetProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [locked, setLocked] = useState(false);
  const rowsRef = useRef<HTMLDivElement>(null);
  const isStreaming = state === 'input-streaming';
  // Le contenu ne se fond depuis le squelette que si ce dernier a été affiché.
  const [sawSkeleton, setSawSkeleton] = useState(isStreaming);
  if (isStreaming && !sawSkeleton) {
    setSawSkeleton(true);
  }
  const effectiveLocked = locked && !hasContinuationError;
  const disabled = isStreaming || isSubmitting || effectiveLocked;

  const submitOutput = (nextOutput: AskUserToolOutputDTO) => {
    if (disabled) {
      return;
    }

    setLocked(true);
    onSubmit(nextOutput);
  };

  const pick = (value: string) => {
    if (disabled) {
      return;
    }

    if (allowMultiple) {
      setSelected((current) => (
        current.includes(value)
          ? current.filter((entry) => entry !== value)
          : [...current, value]
      ));
      return;
    }

    setSelected([value]);
    submitOutput(buildWidgetAskUserOutput([value], options));
  };

  const confirmMulti = () => {
    if (!selected.length) {
      return;
    }

    submitOutput(buildWidgetAskUserOutput(selected, options));
  };

  useAskUserShortcuts({
    count: options.length,
    enabled: state === 'input-available' && !disabled,
    onSelect: (index) => {
      const option = options[index];

      if (option) {
        pick(option.value);
      }
    },
  });

  const moveFocus = (direction: 1 | -1) => {
    const rows = Array.from(
      rowsRef.current?.querySelectorAll<HTMLElement>(`[${ASK_USER_ROW_ATTRIBUTE}]:not(:disabled)`) ?? [],
    );
    const currentIndex = rows.findIndex((row) => row === document.activeElement);
    const nextIndex = currentIndex < 0
      ? 0
      : (currentIndex + direction + rows.length) % rows.length;

    rows[nextIndex]?.focus();
  };

  const titleId = useId();

  return (
    <div
      aria-busy={isStreaming || undefined}
      className="t-ask-rise w-[32rem] max-w-full rounded-[var(--radius-surface-2xl)] border border-border bg-card p-1.5 text-card-foreground dark:bg-muted/30"
      data-testid="ask-user-card"
    >
      {isStreaming ? (
        <>
          <p className="sr-only" role="status">Question en préparation…</p>
          <div className="px-2.5 pb-2 pt-2.5">
            <div className="h-3.5 w-[55%] rounded-md bg-muted/70 motion-safe:animate-pulse" aria-hidden />
          </div>
          <AskUserSkeleton />
        </>
      ) : (
        <div className={cn(sawSkeleton && 't-ask-reveal')}>
          <div className="flex items-start justify-between gap-3 px-2.5 pb-1.5 pt-2.5">
            <p id={titleId} className="text-sm font-medium leading-snug text-foreground">
              {question || 'Question'}
            </p>
            {progress ? (
              <span className="shrink-0 pt-px text-xs tabular-nums text-muted-foreground">
                {progress.index} sur {progress.total}
              </span>
            ) : null}
          </div>

          <div
            ref={rowsRef}
            role={allowMultiple ? 'group' : 'radiogroup'}
            aria-labelledby={titleId}
            className="flex flex-col gap-0.5"
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                moveFocus(event.key === 'ArrowDown' ? 1 : -1);
              }
            }}
          >
            {options.map((option, index) => (
              <OptionRow
                key={option.value}
                disabled={disabled}
                multiple={allowMultiple}
                option={option}
                selected={selected.includes(option.value)}
                shortcut={index + 1}
                onClick={() => pick(option.value)}
              />
            ))}

            {allowFreeText ? (
              <FreeTextRow
                disabled={disabled}
                onSubmit={(text) => submitOutput(buildFreeTextWidgetOutput(text))}
              />
            ) : null}
          </div>

          {allowMultiple ? (
            <p className="sr-only" role="status">
              {selected.length > 0
                ? `${selected.length} option${selected.length > 1 ? 's' : ''} sélectionnée${selected.length > 1 ? 's' : ''}`
                : ''}
            </p>
          ) : null}

          {hasContinuationError ? (
            <div className="px-2.5 pb-1 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-fit rounded-full border-border/70 px-3.5 text-xs shadow-none"
                onClick={onRetry}
              >
                Réessayer l’envoi
              </Button>
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-3 px-2.5 pb-1.5 pt-2">
            <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
              <KeyboardIcon aria-hidden className="size-3.5 shrink-0" />
              <span>Tapez 1 à {options.length}, ou répondez dans le champ de saisie</span>
            </p>
            {allowMultiple ? (
              <Button
                type="button"
                size="sm"
                disabled={!selected.length || disabled}
                onClick={confirmMulti}
                className="h-8 shrink-0 rounded-full px-4 text-xs"
              >
                Valider{selected.length > 0 ? ` (${selected.length})` : ''}
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
